//@ts-check
import {ascend, head, prop, propEq, propOr, sortWith, T} from 'ramda';
import ReactView from './ReactView';

/**
 * @typedef {object} ModuleArgs
 * @prop {string} moduleName
 * @prop {string?} id
 * @prop {import("./ModuleButton")[]?} moduleButtons
 * @prop {(ctx: import("./AppContext")) => boolean} isModuleAllowed
 * @prop {string?} route
 * @prop {string[]?} paramList
 * @prop {React.Component?} module
 * @prop {Backbone.View?} view
 * @prop {boolean} hideHeader
 * @prop {boolean} showNav
 * @prop {string?} title
 * @prop {string?} navId
 * @prop {Function} componentTransform
 * @prop {Function} initializeModule
*/

export const Module = function(/** @type {ModuleArgs} */{
  // Basic Module parameters
  moduleName,           // Name used for module management (Required)
  id = moduleName,      // Unique identifier, duplicates will override existing modules
  moduleButtons = [],   // A list of buttons to be injected in various parts of the UI
  isModuleAllowed,      // A method which can prevent visibility of module (default is visible)

  // Standalone UI Options
  route,                // Route to module, required if the module specifies a view
  paramList,            // List of route params if applicable
  module,               // View module, used to override view options in ReactView
  view,                 // View object, used to shoe-horn Backbone views into the module framework
  hideHeader,           // Whether to show the page header when the view is active
  showNav,              // Whether to show the nav bar when the view is active
  title = moduleName,   // User-facing module title
  navId,                // Which nav item to highlight when this route is active

  // Customization
  componentTransform,   // A method that will be used to extract module-specific items from other modules
  initializeModule,     // A method that will be called with transformed components when live modules are changed
  ...auxiliary          // Any unknown parameters are still passed through
}) {
  const getModuleButtons = () => sortWith([
    ascend(propOr('priority', 100)),
    ascend(prop('text')),
    ascend(prop('id')),
  ], moduleButtons);

  const isAllowedForUser = appContext =>
    !!(
      appContext &&
      (isModuleAllowed || T)(appContext)
    );

  const render = (opts) => {
    if (this.view) {
      this.view.render(opts);
    }
  };

  const getModuleButtonsForType = buttonType =>
    this.moduleButtons
      .filter(propEq('buttonType', buttonType));

  const getModuleNavs = () => getModuleButtonsForType('nav');

  // getModuleActions :: Object? -> [ModuleButton]
  const getModuleActions = () => getModuleButtonsForType('action');

  // _getFirstNavButtonId :: () -> String?
  const _getFirstNavButtonId = () => prop('id', head(getModuleNavs()) || {});

  const _buildViewOpts = () => ({
    navId: navId || _getFirstNavButtonId() || moduleName,
    sdaPageTitle: title,
    hideHeader,
  });

  const getModuleRoute = (Backbone, userModel, appContext, el = '#admin-main') => {
    this.view = this.view || ReactView(el, module, _buildViewOpts(), {module: this, Backbone, userModel, appContext});
    return {route, title, showNav, module: this.view};
  };

  const setModuleRoute = ({Backbone, userModel, appContext, router, el}) => {
    if (route) {
      router.registerRoute(getModuleRoute(Backbone, userModel, appContext, el));
    }
  };

  Object.assign(this, {
    componentTransform,
    hideHeader,
    id,
    initializeModule,
    module,
    moduleButtons,
    moduleName,
    navId,
    paramList,
    route,
    title,
    view,

    /////////////////
    getModuleActions,
    getModuleButtons,
    getModuleButtonsForType,
    getModuleNavs,
    getModuleRoute,
    isAllowedForUser,
    render,
    setModuleRoute,

    /////////////////

    ...auxiliary,
  });
  this.module = module || this;
};
export default Module;
